import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  IconButton,
  Tabs,
  Tab,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { stationsApi } from '../services/api';
import {
  QUIKQ_BASE,
  STATION_ENDPOINTS,
  newRowId,
  defaultQueryRows,
  buildQueryString,
  buildFullUrl,
} from '../utils/quikqStationSearch';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const Stations = () => {
  const [tab, setTab] = useState(0);
  const [endpointPath, setEndpointPath] = useState('/carriers/stations');
  const [queryRows, setQueryRows] = useState(defaultQueryRows);
  const [rawUrlOverride, setRawUrlOverride] = useState('');
  const [quikqToken, setQuikqToken] = useState('');
  const [quikqUserId, setQuikqUserId] = useState('');
  const [quikqPassword, setQuikqPassword] = useState('');
  const [quikqStartCode, setQuikqStartCode] = useState('');
  const [clientListFilter, setClientListFilter] = useState('');

  const [configured, setConfigured] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState({});

  const previewUrl = useMemo(() => {
    if (rawUrlOverride.trim()) return rawUrlOverride.trim();
    return buildFullUrl(endpointPath, queryRows);
  }, [endpointPath, queryRows, rawUrlOverride]);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await stationsApi.getConfigStatus();
      setConfigured(!!data.configured);
    } catch {
      setConfigured(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const updateRow = (id, field, value) => {
    setQueryRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const addQueryRow = () => {
    setQueryRows((rows) => [...rows, { id: newRowId(), key: '', value: '', description: '' }]);
  };

  const removeQueryRow = (id) => {
    setQueryRows((rows) => rows.filter((r) => r.id !== id));
  };

  const resetQueryTemplate = () => setQueryRows(defaultQueryRows());

  const handleSend = async (e) => {
    e?.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);
    setExpanded({});

    const quikqPostmanUrl = rawUrlOverride.trim() || previewUrl;
    const hasToken = quikqToken.trim().length > 0;
    const hasInlineQuikq =
      quikqUserId.trim() && quikqPassword && quikqStartCode.trim();

    const body = {
      quikqPostmanUrl,
      ...(hasToken ? { quikqToken: quikqToken.trim() } : {}),
      ...(hasInlineQuikq && !hasToken
        ? {
            quikqUserId: quikqUserId.trim(),
            quikqPassword,
            quikqStartCode: quikqStartCode.trim(),
          }
        : {}),
    };

    try {
      const data = await stationsApi.search(body);
      setResult(data);
    } catch (err) {
      const apiMsg = err.response?.data?.message || err.response?.data?.error;
      setError(apiMsg || err.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (code) => {
    setExpanded((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const hasToken = quikqToken.trim().length > 0;
  const hasInlineQuikq =
    quikqUserId.trim() && quikqPassword && quikqStartCode.trim();
  const canAuthenticate = configured === true || hasInlineQuikq || hasToken;

  const filteredStations = useMemo(() => {
    const list = result?.stations;
    if (!Array.isArray(list)) return [];
    const needle = clientListFilter.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((s) => {
      const hay = [s.address, s.stationName, s.store, s.city, s.state, s.zip, s.stationCode]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [result, clientListFilter]);

  const listCount = filteredStations.length;
  const totalCount = result?.stations?.length ?? 0;

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Stations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Build the same GET as in Postman: fixed base <code>{QUIKQ_BASE}</code>, path selection, parameters in the
        table, then <strong>Authorization</strong> (Bearer token or QuikQ login / env). <strong>Send</strong> runs
        the request through the admin server.
      </Typography>

      {configured === false && !hasInlineQuikq && !hasToken && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No env credentials on the server. In the <strong>Authorization</strong> tab, enter a Bearer token or
          QuikQ login.
        </Alert>
      )}

      <Paper
        component="form"
        onSubmit={handleSend}
        elevation={2}
        sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
      >
        {/* Request bar — Postman-style */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            bgcolor: 'grey.100',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Chip label="GET" color="success" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', flexShrink: 0 }}>
            {QUIKQ_BASE}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 220, flex: '1 1 200px' }}>
            <InputLabel id="endpoint-label">Path</InputLabel>
            <Select
              labelId="endpoint-label"
              label="Path"
              value={endpointPath}
              onChange={(e) => setEndpointPath(e.target.value)}
              disabled={!!rawUrlOverride.trim()}
            >
              {STATION_ENDPOINTS.map((opt) => (
                <MenuItem key={opt.path} value={opt.path}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !canAuthenticate}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ minWidth: 100 }}
          >
            {loading ? 'Sending…' : 'Send'}
          </Button>
        </Box>

        <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Full URL (preview)
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              wordBreak: 'break-all',
              color: 'text.primary',
            }}
          >
            {previewUrl}
          </Typography>
        </Box>

        <Box sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Query Params" />
            <Tab label="Authorization" />
            <Tab label="Advanced" />
          </Tabs>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <TabPanel value={tab} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Query Params</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={resetQueryTemplate}>
                  Suggested template
                </Button>
                <Button size="small" startIcon={<AddIcon />} onClick={addQueryRow}>
                  Add row
                </Button>
              </Box>
            </Box>
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell width="22%">Key</TableCell>
                    <TableCell width="28%">Value</TableCell>
                    <TableCell width="42%">Description</TableCell>
                    <TableCell width="8%" align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queryRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ py: 0.5, verticalAlign: 'top' }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Key"
                          value={row.key}
                          onChange={(e) => updateRow(row.id, 'key', e.target.value)}
                          disabled={!!rawUrlOverride.trim()}
                          variant="standard"
                          InputProps={{ disableUnderline: false }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, verticalAlign: 'top' }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Value"
                          value={row.value}
                          onChange={(e) => updateRow(row.id, 'value', e.target.value)}
                          disabled={!!rawUrlOverride.trim()}
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, verticalAlign: 'top' }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Description"
                          value={row.description}
                          onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                          disabled={!!rawUrlOverride.trim()}
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                        <Tooltip title="Delete row">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => removeQueryRow(row.id)}
                              disabled={!!rawUrlOverride.trim()}
                              aria-label="delete row"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Typography variant="subtitle2" gutterBottom>
              Bearer token
            </Typography>
            <TextField
              label="QuikQ JWT / Bearer token"
              fullWidth
              size="small"
              multiline
              minRows={3}
              maxRows={10}
              autoComplete="off"
              value={quikqToken}
              onChange={(e) => setQuikqToken(e.target.value)}
              placeholder="eyJ… (optional Bearer prefix)"
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle2" gutterBottom>
              Or QuikQ login (if you have no token)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                label="Username"
                size="small"
                value={quikqUserId}
                onChange={(e) => setQuikqUserId(e.target.value)}
                sx={{ flex: '1 1 200px' }}
              />
              <TextField
                label="Password"
                type="password"
                size="small"
                value={quikqPassword}
                onChange={(e) => setQuikqPassword(e.target.value)}
                sx={{ flex: '1 1 200px' }}
              />
              <TextField
                label="Start code"
                size="small"
                value={quikqStartCode}
                onChange={(e) => setQuikqStartCode(e.target.value)}
                sx={{ flex: '1 1 200px' }}
              />
            </Box>
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Typography variant="subtitle2" gutterBottom>
              Manual URL (overrides path + query table)
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={rawUrlOverride}
              onChange={(e) => setRawUrlOverride(e.target.value)}
              placeholder={`https://api.quikq.com/v1/carriers/stations?...`}
              helperText="When set, Send uses only this URL (same as copying from Postman)."
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle2" gutterBottom>
              List filter (client-side, after response)
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={clientListFilter}
              onChange={(e) => setClientListFilter(e.target.value)}
              placeholder="Substring in name, address, city…"
              disabled={!result?.stations?.length}
            />
          </TabPanel>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {result.mode === 'postman' ? 'Response' : 'Result'} · {listCount} station{listCount === 1 ? '' : 's'}
            {clientListFilter.trim() && totalCount !== listCount ? ` (of ${totalCount} total)` : ''}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
            GET: {result.endpoint}
            {result.authMode === 'token'
              ? ' · auth: Bearer token'
              : result.authMode === 'request'
                ? ' · auth: login form'
                : result.authMode === 'env'
                  ? ' · auth: server env'
                  : ''}
          </Typography>
          {result.query && (
            <Box
              component="pre"
              sx={{
                fontSize: 11,
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
                overflow: 'auto',
                mb: 2,
              }}
            >
              {JSON.stringify(result.query, null, 2)}
            </Box>
          )}

          {filteredStations.length === 0 && !result.quikqResponse && (
            <Alert severity="info">No stations to display.</Alert>
          )}

          {result.mode === 'postman' && filteredStations.length === 0 && result.quikqResponse && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No <code>data.stations</code> in the response. Full JSON below.
            </Alert>
          )}

          {result.mode === 'postman' && result.quikqResponse && filteredStations.length === 0 && (
            <Box
              component="pre"
              sx={{
                fontSize: 12,
                bgcolor: 'grey.100',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 480,
              }}
            >
              {JSON.stringify(result.quikqResponse, null, 2)}
            </Box>
          )}

          {filteredStations.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={48} />
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell align="right">Miles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStations.map((s, idx) => {
                    const rowKey = s.stationCode ? `${s.stationCode}-${idx}` : `st-${idx}`;
                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(rowKey)}
                              aria-label="expand"
                            >
                              {expanded[rowKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{s.stationCode}</TableCell>
                          <TableCell>{s.stationName}</TableCell>
                          <TableCell>
                            {s.city}, {s.state}
                          </TableCell>
                          <TableCell>{s.address}</TableCell>
                          <TableCell align="right">
                            {s.miles != null ? Number(s.miles).toFixed(2) : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                            <Collapse in={!!expanded[rowKey]} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Station JSON
                                </Typography>
                                <Box
                                  component="pre"
                                  sx={{
                                    mt: 1,
                                    fontSize: 11,
                                    maxHeight: 360,
                                    overflow: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {JSON.stringify(s, null, 2)}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default Stations;
